import { endOfMonth, startOfMonth } from 'date-fns';
import { useState, useCallback, useMemo, useEffect,useRef } from 'react'
import { useToastInfo } from '../../../utils/hooks/useToastInfo';
import { useFetch } from '../../../utils/hooks/useFetch';
import { getEmployeeDashboardServiceConfig,getMonthlyStatisticsReqConfig } from '../../../utils/services/dashboard';
import { DayPilot } from '@daypilot/daypilot-lite-react';
import { getYearFormatter,formatTimeEntriesForReports, getWeekRange, getWeekIdentifierString } from '../../../utils/helpers';
import { DASHBOARD_EVENT_MAPPINGS } from '../styles';
import { useNavigate } from 'react-router-dom';
import { SITE_LINKS_SCHEMA } from '../../../router/navlinks';
import { exportTimesheetToPDF, generateTimesheetExcel } from '../../../utils/reports';
export const useEmployeeDashBoard = () => {
    const { isLoading, performAction } = useFetch();
    const { isLoading:isEventsLoading, performAction:performEventsAction } = useFetch();
    const curMonthInfo = useMemo(() => {
        return {
            firstDay: startOfMonth(new Date()),
            lastDay: endOfMonth(new Date())
        }
    }, [])
    const [events,setEvents] = useState([]);
    const groupedProjectsData = useRef([]);
    const { toastInfo, setToastInfo } = useToastInfo();
    const [monthStatistics,setMonthStatistics] = useState(null);
    const navigate = useNavigate();
    const [selectedMonth, setSelectedMonth] = useState(null);
    const handleMonthChange = useCallback((month) => {
        setSelectedMonth(month);
    }, []);
    const handleToastClose = useCallback(()=>{
        setToastInfo(null);
    },[setToastInfo])
    const handleEventSelection = useCallback((eventData)=>{
        const {timesheetId} = eventData;
        const timesheetCorrectionPage = SITE_LINKS_SCHEMA.TIME_SHEET_CORRECTION_DETAILS.replace(':timesheetId',timesheetId);
        navigate(timesheetCorrectionPage);
    },[navigate])
    const fetchEvents = useCallback(async (month) => {
        try {
            const { url, options } = getEmployeeDashboardServiceConfig(month);
            const response = await performEventsAction(url, options);
            if (Array.isArray(response?.Result)) {
                const filteredResponse = response.Result.filter(result => {
                    return result.Projects?.length
                })
                const rawEventsData = filteredResponse.map(res => {
                    const identifier = getYearFormatter(res.Workdate)
                    const weekRange = getWeekRange(identifier);
                    const weekIdentifierString = getWeekIdentifierString(weekRange);
                    return {
                        date: DayPilot.Date(res.Workdate),
                        identifier,
                        tasks: res.Projects.map(prj => {
                            return {
                                status: prj.Status,
                                timesheetId:prj.TimesheetID,
                                projectId:prj.ProjectID,
                                comments:prj.Comments,
                                week_key:weekIdentifierString,
                                approverComments:prj.ApproverComments,
                                comparator:identifier,
                                managerName:prj.ManagerName,
                                managerEmail:prj.ManagerEmail,
                                projectName:prj.ProjectName,
                                poStartDate:prj.POStartDate?prj.POStartDate:"",
                                poEndDate:prj.POEndDate ? prj.POEndDate :'',
                                resourceName:prj.ResourceName,
                                hiringManager:prj.RecruiterName || 'N/A',
                                clientName:prj.ClientName || 'N/A',
                                workLocation:prj.ClientAddress || 'N/A',
                                lastApprovedOn:(prj.LastApprovedOn || "").startsWith('0001') ? 'N/A':prj.LastApprovedOn,
                                lastSubmittedOn:(prj.LastSubmittedOn || "").startsWith('0001') ? 'N/A':prj.LastSubmittedOn,
                                taskInfoDetails:prj.Details.map(detail => {
                                    return {
                                        ProjectTaskName:detail.ProjectTaskName,
                                        ProjectTaskID:detail.ProjectTaskID,
                                        Hours:detail.TaskHours,
                                        Date:identifier,
                                        PayCode:detail.PayCode,
                                        TaskType:detail.TaskType
                                    }
                                }),
                                taskDetails: prj.Details.filter(det=>det.TaskHours>0).reduce((rv,cv)=>{
                                    const prevIndex = rv.findIndex(task=>task.ProjectTaskID === cv.ProjectTaskID);
                                    if(prevIndex>-1){
                                        rv[prevIndex].TaskHours+=cv.TaskHours;
                                    }
                                    else{
                                        rv.push(cv);
                                    }
                                    return rv;
                                },[]).map(detail => {
                                    return {
                                        text: `${detail.ProjectTaskName} ${detail.TaskHours}h`,
                                        taskId: `${identifier}-${detail.ProjectTaskName}-${detail.TaskHours} -${detail.DetailID}`
                                    }
                                })
                            }
                        })
                    }
                })
                const formattedExcelData = rawEventsData.map((event) => {
                    return event.tasks.map(task => {
                        return task.taskInfoDetails.map((detail) => {
                            return {
                                ...task,
                                ...detail
                            }
                        })
                    }).flat()
                }).flat()
                const groupedData = formattedExcelData.reduce((acc,cur)=>{
                    const prevIndex = acc.findIndex(record=>record.projectId === cur.projectId && record.timesheetId === cur.timesheetId);
                    if(prevIndex>-1){
                        acc[prevIndex].taskInfoDetails.push(...cur.taskInfoDetails);
                    }
                    else{
                        acc.push(cur);
                    }
                    return acc;
                },[])
                const formattedGroupedData = groupedData.map(dt=>{
                    return {
                        ...dt,
                        timesheetData:formatTimeEntriesForReports(dt.taskInfoDetails)
                    }
                })
                groupedProjectsData.current = formattedGroupedData;
                const formattedEvents = rawEventsData.map((event) => {
                    return event.tasks.map(task => {
                        return task.taskDetails.map((detail) => {
                            const styleObject = DASHBOARD_EVENT_MAPPINGS[task.status?.toUpperCase()] || {}
                            return {
                                date: event.date,
                                start:event.date,
                                end:event.date,
                                identifier: event.identifier,
                                id: detail.taskId,
                                timesheetId:task.timesheetId,
                                text: detail.text,
                                status: task.status,
                                ...styleObject
                            }
                        })
                    }).flat()
                }).flat()
              setEvents(formattedEvents);
            }
        }
        catch (e) {
            console.error('Errror fetching events');
            setToastInfo({
                message: e.message,
                type: "error",
                autoExpire: true
            })
        }
    }, [performEventsAction, setToastInfo])
    const fetchMonthlyStatistics = useCallback(async(month)=>{
        try{
            const {url,options} = getMonthlyStatisticsReqConfig(month);
            const response = await performAction(url,options);
            if(response.Result && typeof (response.Result) === "object"){
                const {TotalPossibleHours,TotalReportedHours,TotalVacationHours,TotalApprovedHours} = response.Result;
                setMonthStatistics({
                    totalHours:TotalPossibleHours,
                    reportedHours:TotalReportedHours,
                    vacationHours:TotalVacationHours,
                    approvedHours:TotalApprovedHours
                })
                return true;
            }
            throw new Error("Unable to fetch the counts");
        }   
        catch(e){
            console.error('Error fetching monthly count');
            setToastInfo({
                message:e.message,
                type:"error",
                autoExpire:true
            })
        }
    },[performAction,setToastInfo])
    const generateTimesheetReport = (format)=>{
        if(groupedProjectsData.current?.length){
            if(format === "excel"){
                generateTimesheetExcel(groupedProjectsData.current);
            }
            else{
                exportTimesheetToPDF(groupedProjectsData.current)
            }
        } 
    }
    useEffect(() => {
        if (selectedMonth) {
            fetchEvents(selectedMonth)
            fetchMonthlyStatistics(selectedMonth);
        }
    }, [selectedMonth, fetchEvents,fetchMonthlyStatistics])
    return {
        selectedMonth,
        events,
        handleMonthChange,
        handleEventSelection,
        monthStatistics,
        curMonthInfo,
        toastInfo,
        handleToastClose,
        generateTimesheetReport,
        isLoading:isLoading || isEventsLoading
    }
}
